import { Button, Modal, SelectField, TextAreaField, TextField, WriteError } from '@shared/ui';
import { useState } from 'react';

import {
    componentCreateInputFrom,
    EMPTY_COMPONENT_DRAFT,
    parseMemberList,
    type ComponentDraft,
} from '../api/component-create';
import {
    componentTypeCopy,
    isReleaseVersion,
    releaseCreateInputFrom,
    type ComponentType,
} from '../api/release-create';
import { useCreateComponent } from '../hooks/use-components';
import { useCreateRelease } from '../hooks/use-release-components';
import { explainComponentIdRejection, rejectComponentId } from '../schemas/component-id';

import { explainComponentWriteFailure } from './component-error-copy';
import { explainReleaseWriteFailure } from './release-error-copy';

import type { Component } from '../schemas/component';
import type { ReleaseComponent } from '../schemas/release-component';

/** One draft for both types, so switching the selector keeps what was typed. */
type DialogDraft = ComponentDraft & { releaseVersion: string };

const EMPTY_DIALOG_DRAFT: DialogDraft = { ...EMPTY_COMPONENT_DRAFT, releaseVersion: '' };

/**
 * Define a component.
 *
 * ## Why this is one screen and not the flag dialog's two
 *
 * `CreateFlagDialog` makes the operator confirm the key on a screen of its own,
 * because a typo'd flag key is a permanent row that gates nothing and looks
 * exactly as legitimate as the flag that was meant. A component id is permanent
 * in the same way — grants are written under it — but the failure mode is not
 * the same: a component with a wrong id has a display name, a description and a
 * member list beside it on every screen that shows it, so it is visibly the
 * wrong thing rather than an invisible near-duplicate. A second screen here
 * would be ceremony copied rather than reasoned, and the id rule is mirrored
 * client-side so a malformed one never reaches the button at all.
 *
 * ## The member list is a textarea
 *
 * Not a picker over the flags query, for this slice. An entitlement flag need
 * not exist when its bundle is assembled, a bundle arrives in practice as a
 * pasted list from a ticket, and the API is the only thing that can say whether
 * a key names an entitlement flag — so a picker would have to load and filter
 * the whole catalog to end up asking the server the same question anyway. It
 * does mean an operator finds out about a bad member on submit; see
 * `component-error-copy.ts`, which is where that answer is made readable. A
 * picker is the obvious next improvement once this tab has a flags query of its
 * own to reuse.
 *
 * Nothing rendered here is testable — vitest runs on `node` with no DOM — so
 * the payload, the id rule and the failure copy all live in modules beside it.
 */
export function CreateComponentDialog({
    open,
    onClose,
    onCreated,
}: {
    open: boolean;
    onClose: () => void;
    onCreated: (component: Component | ReleaseComponent) => void;
}) {
    const [draft, setDraft] = useState<DialogDraft>(EMPTY_DIALOG_DRAFT);
    const [type, setType] = useState<ComponentType>('commercial');
    const createCommercial = useCreateComponent();
    const createRelease = useCreateRelease();
    const isRelease = type === 'release';
    const copy = componentTypeCopy(type);
    // Only the active type's mutation drives the dialog; the other stays idle.
    const create = isRelease ? createRelease : createCommercial;

    const set = <K extends keyof DialogDraft>(field: K, value: DialogDraft[K]) => {
        setDraft((current) => ({ ...current, [field]: value }));
    };

    const close = () => {
        setDraft(EMPTY_DIALOG_DRAFT);
        setType('commercial');
        createCommercial.reset();
        createRelease.reset();
        onClose();
    };

    const rejection = rejectComponentId(draft.id);
    // An untouched field is not yet a mistake — showing "an id is required" on
    // an empty form scolds the operator for not having typed anything yet.
    const idError =
        draft.id.length > 0 && rejection ? explainComponentIdRejection(rejection) : undefined;

    const commercialInput = componentCreateInputFrom(draft);
    const releaseInput = releaseCreateInputFrom(draft);
    const canSubmit = isRelease ? releaseInput !== null : commercialInput !== null;
    const memberCount = parseMemberList(draft.members).length;
    const failure = create.error
        ? isRelease
            ? explainReleaseWriteFailure(create.error)
            : explainComponentWriteFailure(create.error)
        : null;

    const submit = () => {
        if (isRelease) {
            if (releaseInput === null) return;
            createRelease.mutate(releaseInput, { onSuccess: onCreated });
        } else {
            if (commercialInput === null) return;
            createCommercial.mutate(commercialInput, { onSuccess: onCreated });
        }
    };

    return (
        <Modal
            open={open}
            onClose={close}
            dismissable={!create.isPending}
            title={copy.title}
            description={copy.description}
            footer={
                <>
                    <Button variant="ghost" onClick={close} disabled={create.isPending}>
                        Cancel
                    </Button>
                    <Button onClick={submit} disabled={!canSubmit || create.isPending}>
                        {create.isPending ? 'Creating…' : copy.submitLabel}
                    </Button>
                </>
            }
        >
            <div className="flex flex-col gap-4">
                <SelectField
                    label="Type"
                    value={type}
                    onChange={(value) => {
                        setType(value);
                        createCommercial.reset();
                        createRelease.reset();
                    }}
                    options={[
                        { value: 'commercial', label: 'Commercial' },
                        { value: 'release', label: 'Release' },
                    ]}
                    hint="Commercial components are sold to museums. A release component groups the flags that ship together."
                />
                <TextField
                    label="Id"
                    value={draft.id}
                    onChange={(value) => {
                        set('id', value);
                    }}
                    placeholder="membership"
                    mono
                    autoFocus
                    error={idError}
                    hint={
                        isRelease
                            ? 'Permanent. It is the page address, so it is never renamed or reused. Letters, digits, `_` and `-` — no dots.'
                            : 'Permanent. Grants are written under it, so it is never renamed or reused. Letters, digits, `_` and `-` — no dots.'
                    }
                />
                <TextField
                    label={isRelease ? 'Name' : 'Display name'}
                    value={draft.displayName}
                    onChange={(value) => {
                        set('displayName', value);
                    }}
                    placeholder={isRelease ? 'Spring launch' : 'Membership'}
                    hint={
                        isRelease
                            ? 'What the release is called. Like the id, it cannot be changed after creation.'
                            : 'What Product and Sales call it. Unlike the id, this can be changed.'
                    }
                />
                <TextAreaField
                    label={isRelease ? 'Description' : 'What it sells'}
                    value={draft.description}
                    onChange={(value) => {
                        set('description', value);
                    }}
                    placeholder={
                        isRelease
                            ? 'What ships in this release?'
                            : 'What does a museum get when this is activated?'
                    }
                />
                {!isRelease && (
                    <TextField
                        label="Owning team"
                        value={draft.owner}
                        onChange={(value) => {
                            set('owner', value);
                        }}
                        placeholder="Commercial"
                        hint="The team accountable for the capability, not for this catalog row."
                    />
                )}
                <TextAreaField
                    label={copy.membersLabel}
                    value={draft.members}
                    onChange={(value) => {
                        set('members', value);
                    }}
                    rows={4}
                    placeholder={
                        isRelease
                            ? 'checkout.new-flow\nsync.salesforce'
                            : 'membership.renewals\nmembership.tiers'
                    }
                    hint={
                        <>
                            {copy.membersHint}
                            {memberCount > 0 && ` ${String(memberCount)} so far.`}
                        </>
                    }
                />
                {isRelease && (
                    <TextField
                        label="Release version"
                        value={draft.releaseVersion}
                        onChange={(value) => {
                            set('releaseVersion', value);
                        }}
                        placeholder="2026.10.1"
                        mono
                        error={
                            draft.releaseVersion.trim().length > 0 &&
                            !isReleaseVersion(draft.releaseVersion)
                                ? 'Use 1-32 characters of letters, digits, `.`, `_` or `-`.'
                                : undefined
                        }
                        hint="The label for this release. It can be changed later."
                    />
                )}
                {!isRelease && (
                    <div className="grid grid-cols-2 gap-4">
                        <TextField
                            label="Owner service (optional)"
                            value={draft.ownerService}
                            onChange={(value) => {
                                set('ownerService', value);
                            }}
                            placeholder="https://membership.internal"
                            hint="Only for a component whose own service has to provision something."
                        />
                        <TextField
                            label="Activation path (optional)"
                            value={draft.activationPath}
                            onChange={(value) => {
                                set('activationPath', value);
                            }}
                            placeholder="/activate"
                            mono
                        />
                    </div>
                )}
                <TextAreaField
                    label="Reason (optional)"
                    value={draft.reason}
                    onChange={(value) => {
                        set('reason', value);
                    }}
                    rows={2}
                    placeholder={
                        isRelease
                            ? 'Why this release is being created'
                            : 'Why this component is being created'
                    }
                    hint="Recorded on the audit entry."
                />

                {/* Two panels, not one. The shared `WriteError` already words a
                    403, a version conflict and a validation `issues` list the
                    way every other screen does; the codes it cannot word — two
                    409s that mean opposite things — get the panel below. */}
                {failure ? (
                    <div
                        role="alert"
                        className="rounded-lg border border-danger-line bg-danger-surface px-3.5 py-2.5 text-[12.5px] leading-relaxed text-danger-text"
                    >
                        <p className="font-semibold">{failure.title}</p>
                        <p className="mt-0.5 text-danger-muted">{failure.body}</p>
                        {failure.detail !== undefined && (
                            <p className="mt-1.5 text-danger-muted">{failure.detail}</p>
                        )}
                    </div>
                ) : (
                    create.error && <WriteError error={create.error} />
                )}
            </div>
        </Modal>
    );
}
